// §73 Inc 6 – <drive-map>: the live GPS trail canvas.
//
// A pure rendering SINK, not a state owner. Datastar stays the single data flow:
// GPS fix -> the recorder's report arrow -> the `_drvLast` signal -> `data-attr:fix`
// on this element -> here. It also takes a one-shot server `points` seed (the trail
// recorded before a resume/reload) and a `paused` flag driven by the visibility
// signal. It owns NO authoritative state and never fetches – it just projects and
// draws, the same way `data-text` projects a signal onto a text node.
//
// Why a web component and not the recorder leaf: the trail must stay smooth (rAF,
// not 5 s SSE ticks) and keep drawing OFFLINE (geolocation is local; SSE freezes in
// the tunnel). Canvas is a browser-API surface Datastar has no primitive for, so it
// is a legitimate leaf – provided it reads signals and draws, nothing more.
//
// The one gotcha this design exists to avoid: it accumulates its OWN durable path
// internally, so the 5 s `$_drvPoints` flush (which clears that outbound buffer) never
// blanks what is drawn. `$_drvPoints` is for the POST; `_drvLast` (never cleared) is
// for the trail.
//
// Rocket owns the element plumbing (typed props, lifecycle, cleanup); the drawing
// engine is the plain `Trail` class below. Shadow DOM, so a backend morph of the host
// can never drop the canvas – only the host's attributes (the props) reach it.

import { onThemeChange } from './theme';
import { createCodec, rocket } from '/static/vendor/datastar-rocket.js';

// Metres per degree near Switzerland – good to ~0.1% at trip scale, far below GPS
// noise. Equirectangular local frame anchored at the first point; north is up (-y).
const M_PER_DEG_LAT = 110540;
const M_PER_DEG_LNG = 111320;

type Fix = { lat: number; lng: number; acc?: number; h?: number };

// Tolerant JSON codecs: `data-attr:fix` writes '' until the first fix and the server
// seed is '' when there is no track yet, so garbage decodes to "nothing" rather than
// throwing (Rocket would log a warning per bad value).
const parseJSON = (value: unknown): unknown => {
  if (typeof value !== 'string' || !value) return value ?? null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

// A real WGS84 coordinate: finite and in range (a 0/NaN/garbage fix must never become
// the projection anchor, which would fling the whole trail off-canvas).
const isLatLng = (lat: unknown, lng: unknown): boolean =>
  typeof lat === 'number' && typeof lng === 'number' &&
  Number.isFinite(lat) && Number.isFinite(lng) &&
  Math.abs(lat) <= 90 && Math.abs(lng) <= 180;

// Optional fix fields survive only when meaningful; otherwise they are dropped and the
// accuracy ring / heading cone simply isn't drawn for that fix.
const inRange = (v: unknown, min: number, max: number): v is number =>
  typeof v === 'number' && Number.isFinite(v) && v >= min && v <= max;

const fixCodec = createCodec<Fix | null>({
  decode(value) {
    const f = parseJSON(value) as Record<string, unknown> | null;
    if (!f || typeof f !== 'object' || !isLatLng(f.lat, f.lng)) return null;
    const fix: Fix = { lat: f.lat as number, lng: f.lng as number };
    if (inRange(f.acc, 0, 100_000)) fix.acc = f.acc;
    if (inRange(f.h, 0, 360)) fix.h = f.h;
    return fix;
  },
  encode: (value) => (value ? JSON.stringify(value) : ''),
});

const trackCodec = createCodec<[number, number][]>({
  decode(value) {
    const arr = parseJSON(value);
    if (!Array.isArray(arr)) return [];
    return arr.filter((p): p is [number, number] => Array.isArray(p) && isLatLng(p[0], p[1]));
  },
  encode: (value) => JSON.stringify(value),
});

class Trail {
  #canvas!: HTMLCanvasElement;
  #ctx!: CanvasRenderingContext2D;
  #host: HTMLElement;
  #cw = 0;
  #ch = 0;

  // Durable projected path (metres in the local frame) + its anchor.
  #world: number[][] = [];
  #lat0: number | null = null;
  #lng0 = 0;
  #cos0 = 1;
  #acc = -1;
  #heading = -1;

  // Eased camera (world centre + px-per-metre) and the eased dot position, so the
  // view glides and the marker doesn't teleport between fixes.
  #view = { cx: 0, cy: 0, scale: 0 };
  #dot: [number | null, number] = [null, 0];
  #accent = '#5b8cff';

  constructor(host: HTMLElement) {
    this.#host = host;
  }

  // (Re)bind the drawing surface: called on every connect, since the path outlives it.
  bind(canvas: HTMLCanvasElement): void {
    this.#canvas = canvas;
    this.#ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
    this.resize();
    this.readColors();
  }

  // Resolve --accent to a real rgb() usable by canvas. getPropertyValue returns the
  // token verbatim ("light-dark(…)"), so probe through a real `color` property on the
  // canvas (custom properties inherit into the shadow root, so light-dark resolves
  // in-context). Mirrors the chart's resolveColor probe.
  readColors(): void {
    this.#canvas.style.color = 'var(--accent)';
    const c = getComputedStyle(this.#canvas).color;
    if (c) this.#accent = c;
  }

  // One-shot server seed; a no-op once seeded or live.
  seed(points: [number, number][]): void {
    if (this.#world.length) return;
    for (const [lat, lng] of points) this.#add(lat, lng);
  }

  push(f: Fix): void {
    if (typeof f.acc === 'number') this.#acc = f.acc;
    if (typeof f.h === 'number') this.#heading = f.h;
    this.#add(f.lat, f.lng);
  }

  frame(): void {
    if (this.#cw === 0 || this.#ch === 0 || !this.#world.length) return;
    this.#step();
    this.#draw();
  }

  resize(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    const r = this.#host.getBoundingClientRect();
    this.#cw = r.width;
    this.#ch = r.height;
    this.#canvas.width = Math.max(1, Math.round(r.width * dpr));
    this.#canvas.height = Math.max(1, Math.round(r.height * dpr));
    this.#ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  #add(lat: number, lng: number): void {
    if (this.#lat0 === null) {
      this.#lat0 = lat;
      this.#lng0 = lng;
      this.#cos0 = Math.cos((lat * Math.PI) / 180);
    }
    const x = (lng - this.#lng0) * this.#cos0 * M_PER_DEG_LNG;
    const y = -(lat - this.#lat0) * M_PER_DEG_LAT;
    this.#world.push([x, y]);
  }

  // Ease the camera to frame the whole trail and the dot to the latest fix.
  #step(): void {
    let minx = Infinity;
    let miny = Infinity;
    let maxx = -Infinity;
    let maxy = -Infinity;
    for (const [x, y] of this.#world) {
      if (x < minx) minx = x;
      if (x > maxx) maxx = x;
      if (y < miny) miny = y;
      if (y > maxy) maxy = y;
    }
    const MIN_SPAN = 160; // metres – bounds the zoom when the trail is tiny
    const PAD = 1.3;
    const spanX = Math.max((maxx - minx) * PAD, MIN_SPAN);
    const spanY = Math.max((maxy - miny) * PAD, MIN_SPAN);
    const target = Math.max(0.05, Math.min(this.#cw / spanX, this.#ch / spanY, 4));
    const tcx = (minx + maxx) / 2;
    const tcy = (miny + maxy) / 2;
    if (this.#view.scale === 0) {
      this.#view = { cx: tcx, cy: tcy, scale: target }; // snap on first frame
    } else {
      this.#view.cx += (tcx - this.#view.cx) * 0.12;
      this.#view.cy += (tcy - this.#view.cy) * 0.12;
      this.#view.scale += (target - this.#view.scale) * 0.12;
    }
    const last = this.#world[this.#world.length - 1];
    if (this.#dot[0] === null) {
      this.#dot = [last[0], last[1]];
    } else {
      this.#dot[0] += (last[0] - this.#dot[0]) * 0.25;
      this.#dot[1] += (last[1] - this.#dot[1]) * 0.25;
    }
  }

  #sx(x: number): number {
    return this.#cw / 2 + (x - this.#view.cx) * this.#view.scale;
  }

  #sy(y: number): number {
    return this.#ch / 2 + (y - this.#view.cy) * this.#view.scale;
  }

  #draw(): void {
    const ctx = this.#ctx;
    ctx.clearRect(0, 0, this.#cw, this.#ch);
    const W = this.#world;
    if (W.length >= 2) {
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      // Smooth the trail: quadratic curves through the segment midpoints round off the
      // GPS jitter (and any discrete-fix staircase) into a flowing line, instead of raw
      // straight segments between every fix.
      const P = W.map(([x, y]) => [this.#sx(x), this.#sy(y)]);
      ctx.beginPath();
      ctx.moveTo(P[0][0], P[0][1]);
      if (P.length < 3) {
        ctx.lineTo(P[1][0], P[1][1]);
      } else {
        for (let i = 1; i < P.length - 1; i++) {
          ctx.quadraticCurveTo(P[i][0], P[i][1], (P[i][0] + P[i + 1][0]) / 2, (P[i][1] + P[i + 1][1]) / 2);
        }
        ctx.lineTo(P[P.length - 1][0], P[P.length - 1][1]);
      }
      // Soft glow underlay, then a crisp core line.
      ctx.strokeStyle = this.#accent;
      ctx.globalAlpha = 0.3;
      ctx.lineWidth = 6;
      ctx.shadowColor = this.#accent;
      ctx.shadowBlur = 14;
      ctx.stroke();
      ctx.shadowBlur = 0;
      ctx.globalAlpha = 0.92;
      ctx.lineWidth = 2.5;
      ctx.stroke();
      ctx.globalAlpha = 1;
    }
    if (this.#dot[0] === null) return;
    const dx = this.#sx(this.#dot[0]);
    const dy = this.#sy(this.#dot[1]);
    // Accuracy ring – radius = accuracy in metres, floored so it stays visible.
    if (this.#acc > 0) {
      const rr = Math.max(this.#acc * this.#view.scale, 7);
      ctx.beginPath();
      ctx.arc(dx, dy, rr, 0, Math.PI * 2);
      ctx.fillStyle = this.#accent;
      ctx.globalAlpha = 0.08;
      ctx.fill();
      ctx.globalAlpha = 0.5;
      ctx.lineWidth = 1;
      ctx.strokeStyle = this.#accent;
      ctx.stroke();
      ctx.globalAlpha = 1;
    }
    // Heading cone – a wedge pointing the way we're moving (north = up).
    if (this.#heading >= 0) {
      const a = ((this.#heading - 90) * Math.PI) / 180;
      const spread = 0.45;
      const R = 38;
      ctx.beginPath();
      ctx.moveTo(dx, dy);
      ctx.arc(dx, dy, R, a - spread, a + spread);
      ctx.closePath();
      ctx.fillStyle = this.#accent;
      ctx.globalAlpha = 0.28;
      ctx.fill();
      ctx.globalAlpha = 1;
    }
    // Current-position dot.
    ctx.beginPath();
    ctx.arc(dx, dy, 6, 0, Math.PI * 2);
    ctx.fillStyle = '#fff';
    ctx.shadowColor = this.#accent;
    ctx.shadowBlur = 12;
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.beginPath();
    ctx.arc(dx, dy, 6, 0, Math.PI * 2);
    ctx.lineWidth = 2;
    ctx.strokeStyle = this.#accent;
    ctx.stroke();
  }
}

const trails = new WeakMap<HTMLElement, Trail>();

rocket('drive-map', {
  mode: 'open',
  props: ({ bool }) => ({
    fix: fixCodec,
    points: trackCodec,
    paused: bool,
  }),
  // Everything after the first render is imperative canvas work; props never re-render.
  renderOnPropChange: false,
  setup: ({ adoptStyles, host }) => {
    adoptStyles(host, ':host { display: block; } canvas { display: block; width: 100%; height: 100%; }');
  },
  render: ({ html }) => html`<canvas data-ref:canvas></canvas>`,
  onFirstRender: ({ refs, host, props, observeProps, cleanup }) => {
    const canvas = refs.canvas as HTMLCanvasElement | undefined;
    if (!canvas) return;
    // onFirstRender runs on every connect; a reconnect (DOM move) keeps the drawn path.
    let trail = trails.get(host);
    if (!trail) {
      trail = new Trail(host);
      trails.set(host, trail);
      trail.seed(props.points);
      if (props.fix) trail.push(props.fix);
    }
    trail.bind(canvas);
    const t = trail;
    observeProps(() => t.seed(props.points), 'points');
    observeProps(() => props.fix && t.push(props.fix), 'fix');

    const ro = new ResizeObserver(() => t.resize());
    ro.observe(host);
    const offTheme = onThemeChange(() => t.readColors());
    let raf = 0;
    const loop = (): void => {
      raf = requestAnimationFrame(loop);
      if (!props.paused) t.frame();
    };
    raf = requestAnimationFrame(loop);

    cleanup(() => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      offTheme();
    });
  },
});
